import { useEffect, useState } from "react";
import { Shield } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { fetchAdminStats } from "@/lib/api";

export const ADMIN_COUNT_FIELDS = [
  { key: "registered_users", label: "Registered users", testId: "admin-registered-users" },
  { key: "new_users_this_month", label: "New users this month", testId: "admin-new-users-this-month" },
  { key: "active_users_7d", label: "Active users, 7 days", testId: "admin-active-users-7d" },
  { key: "active_users_30d", label: "Active users, 30 days", testId: "admin-active-users-30d" },
  { key: "strava_connected_users", label: "Strava-connected users", testId: "admin-strava-connected-users" },
  { key: "developer_accounts", label: "Developer accounts", testId: "admin-developer-accounts" },
];

/** Keep only the six aggregate numbers and the count time. */
export function adminCountView(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const counts = {};
  for (const field of ADMIN_COUNT_FIELDS) {
    const value = payload[field.key];
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    counts[field.key] = value;
  }
  return {
    counts,
    generatedAt: typeof payload.generated_at === "string" ? payload.generated_at : "",
  };
}

export const AdminPage = () => {
  const [view, setView] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = adminCountView(await fetchAdminStats());
        if (cancelled) return;
        if (!next) {
          setStatus("error");
          return;
        }
        setView(next);
        setStatus("ready");
      } catch (error) {
        if (cancelled) return;
        const code = error?.response?.status;
        if (code === 403) setStatus("denied");
        else if (code === 401) setStatus("signed-out");
        else setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div data-testid="admin-page">
      <PageHeader
        eyebrow="Private"
        title="Admin"
        subtitle="Account totals only. Individual accounts stay hidden."
        icon={Shield}
      />
      {status === "loading" ? <p data-testid="admin-loading">Loading counts…</p> : null}
      {status === "denied" ? (
        <p data-testid="admin-denied">This page is only available to an admin account.</p>
      ) : null}
      {status === "signed-out" ? (
        <p data-testid="admin-signed-out">Sign in again to view account totals.</p>
      ) : null}
      {status === "error" ? (
        <p data-testid="admin-error">The account totals could not be loaded.</p>
      ) : null}
      {status === "ready" && view ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {ADMIN_COUNT_FIELDS.map((field) => (
              <div key={field.key} className="metric-card" data-testid={field.testId}>
                <div className="text-[10px] tracking-[0.16em] uppercase text-dark-text-muted font-semibold">
                  {field.label}
                </div>
                <div className="metric-value mt-2">{view.counts[field.key]}</div>
              </div>
            ))}
          </div>
          {view.generatedAt ? (
            <p className="mt-6 text-xs text-dark-text-muted" data-testid="admin-generated-at">
              Counted at {view.generatedAt}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
